import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface BasicAuthProps {
  children: React.ReactNode;
}

interface BasicAuthContextValue {
  isAuthenticated: boolean;
  logout: () => void;
}

const BasicAuthContext = React.createContext<BasicAuthContextValue | undefined>(
  undefined
);

export function useBasicAuth(): BasicAuthContextValue {
  const ctx = React.useContext(BasicAuthContext);
  if (!ctx) throw new Error("useBasicAuth must be used within BasicAuth");
  return ctx;
}

function BasicAuth({ children }: BasicAuthProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // Check if already authenticated on mount
  useEffect(() => {
    const auth = sessionStorage.getItem("vbt-flow-auth");
    if (auth === "authenticated") {
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Get credentials from environment variables
    const validUsername = import.meta.env.VITE_BASIC_AUTH_USERNAME || "admin";
    const validPassword =
      import.meta.env.VITE_BASIC_AUTH_PASSWORD || "password";

    if (username === validUsername && password === validPassword) {
      setIsAuthenticated(true);
      sessionStorage.setItem("vbt-flow-auth", "authenticated");
      setError("");
    } else {
      setError("Invalid credentials");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("vbt-flow-auth");
    setUsername("");
    setPassword("");
  };

  return (
    <BasicAuthContext.Provider
      value={{ isAuthenticated, logout: handleLogout }}
    >
      {isAuthenticated ? (
        <div>{children}</div>
      ) : (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-center">VBT Flow - Login</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                {error && <div className="text-red-600 text-sm">{error}</div>}
                <Button type="submit" className="w-full">
                  Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </BasicAuthContext.Provider>
  );
}

export default BasicAuth;
